import CryptoJS from "crypto-js";
import { useCallback, useMemo } from "react";
import { useRecoilValue, useResetRecoilState } from "recoil";
import {
  mandatoryItemList,
  substituteItemList,
  substituteSuggestionItemFlag,
} from "src/components/features/cart/confirm/OrderDisclaimer/useRadioState";
import { selectedPaymentMethodState } from "src/components/features/cart/confirm/PaymentSelection";
import { useCallbackOrderSuccess } from "src/components/features/cart/confirm/useCallbackOrderSuccess";
import { useAuthorizeAuPayForDelivery } from "src/modules/api/Order/authorizeAuPayForDelivery/useAuthorizeAuPayForDelivery";
import { useOrderForDeliveryWithAuPay } from "src/modules/api/Order/orderForDeliveryWithAuPay/useOrderForDeliveryWithAuPay";
import { CartData } from "src/modules/domain/cart/CartData";
import { OrderSecure } from "src/modules/domain/order/OrderSecure";
import { PAYMENT_TYPE } from "src/modules/domain/order/payment/paymentType/paymentType";
import { VeritransRedirectQuery } from "src/modules/domain/order/VeritransRedirectQuery";
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from "src/modules/localStorage";

export const useAuPayConfirm = () => {
  const { trigger: authorizeAuPayForDeliveryTrigger } =
    useAuthorizeAuPayForDelivery();
  const { trigger: orderForDeliveryWithAuPay } = useOrderForDeliveryWithAuPay();

  const mandatoryList = useRecoilValue(mandatoryItemList);
  const resetMandatoryList = useResetRecoilState(mandatoryItemList);
  const substituteList = useRecoilValue(substituteItemList);
  const resetSubstituteList = useResetRecoilState(substituteItemList);
  const resetAuPaySelection = useResetRecoilState(selectedPaymentMethodState);
  const substituteSuggestionFlag = useRecoilValue(substituteSuggestionItemFlag);
  const resetsubstituteSuggestionFlag = useResetRecoilState(
    substituteSuggestionItemFlag
  );
  const pontaPointForUseResult = useMemo(() => {
    return getLocalStorage<number>("pontaPointForUse");
  }, []);
  const pontaPointForUse = useMemo(() => {
    return pontaPointForUseResult.isStored ? pontaPointForUseResult.value : 0;
  }, [pontaPointForUseResult]);
  const substituteListParams = useMemo(
    () =>
      substituteList.substituteItemPairs.map((pair) => ({
        cart_item_id: pair.baseCartItemId,
        substitute_shop_item_id: pair.substituteItem.shop_item_id,
      })),
    [substituteList.substituteItemPairs]
  );
  const shopItemShortageParams = useMemo(() => {
    return substituteList.substituteItemPairs.length !== 0
      ? {
          substitute_item_list: substituteListParams,
          is_selected_substitute_item_suggestion: substituteSuggestionFlag,
        }
      : mandatoryList.length !== 0
      ? {
          must_have_cart_item_id_list: mandatoryList,
          is_selected_substitute_item_suggestion: substituteSuggestionFlag,
        }
      : {};
  }, [
    substituteList.substituteItemPairs.length,
    substituteListParams,
    substituteSuggestionFlag,
    mandatoryList,
  ]);

  const onOrderSuccess = useCallbackOrderSuccess();
  const orderWithAuPay = useCallback(
    async (
      orderSecure: OrderSecure,
      veritransDDDSecureQuery: VeritransRedirectQuery
    ) => {
      const {
        veritrans_credit_id,
        used_reason,
        user_delivery_address_id,
        deluxe_request,
        is_allowed_out_of_stock,
      } = orderSecure;
      const res = await orderForDeliveryWithAuPay({
        params: {
          veritrans_credit_id,
          used_reason,
          user_delivery_address_id,
          deluxe_request,
          is_allowed_out_of_stock,
          payment_type: PAYMENT_TYPE.AU,
          veritrans_redirect_query: veritransDDDSecureQuery,
          is_selected_substitute_item_suggestion: substituteSuggestionFlag,
          ...shopItemShortageParams,
        },
      });

      if (res) {
        onOrderSuccess(res.order_no);
        resetMandatoryList();
        resetSubstituteList();
        resetAuPaySelection();
        resetsubstituteSuggestionFlag();
      }
      removeLocalStorage("auPayOrderSecure");
    },
    [
      onOrderSuccess,
      orderForDeliveryWithAuPay,
      resetAuPaySelection,
      resetMandatoryList,
      resetSubstituteList,
      resetsubstituteSuggestionFlag,
      shopItemShortageParams,
      substituteSuggestionFlag,
    ]
  );

  const linkWithAuId = useCallback(
    async (
      cartData: CartData,
      obtain_ponta_point: number,
      obtain_smart_pass_ponta_point: number,
      isOutOfStockAllowed: boolean,
      user_delivery_address_id: string
    ) => {
      removeLocalStorage("auPayOrderSecure");
      const result = await authorizeAuPayForDeliveryTrigger({
        params: {
          cart_id: cartData.cart_id,
          user_delivery_address_id: Number(user_delivery_address_id),
          total_price: cartData.user_payment_price - pontaPointForUse,
          payment_type: PAYMENT_TYPE.AU,
          use_ponta_point: pontaPointForUse,
          obtain_ponta_point: obtain_ponta_point,
          obtain_smart_pass_ponta_point: obtain_smart_pass_ponta_point,
        },
      });
      if (!result) {
        return;
      }
      const orderSecure = OrderSecure.create({
        user_delivery_address_id: user_delivery_address_id,
        veritrans_credit_id: result.veritrans_credit_id,
        is_allowed_out_of_stock: isOutOfStockAllowed,
        total_price: cartData.user_payment_price,
        html: result.content,
        is_target_ddd_authentication: result.is_authentication_required,
      });

      if (result.is_authentication_required) {
        setLocalStorage(
          "auPayOrderSecure",
          encryptAuPayOrderSecureData(orderSecure) || ""
        );
        // NOTE: Open HTML content returned from the server as a link
        const blob = new Blob([result.content], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        window.location.href = url;
        return;
      }
    },
    [authorizeAuPayForDeliveryTrigger, pontaPointForUse]
  );

  return { linkWithAuId, orderWithAuPay };
};

// TODO: replace environment variables
const secretKey = process.env.NEXT_PUBLIC_AU_PAY_ORDER_SECURE_KEY;

// Encrypt and decrypt the orderSecure data to be stored in localStorage
const encryptAuPayOrderSecureData = (data: OrderSecure): string | undefined => {
  if (!secretKey) {
    return undefined;
  }
  return CryptoJS.AES.encrypt(JSON.stringify(data), secretKey).toString();
};

// Decrypt the orderSecure data stored in localStorage
export const decryptAuPayOrderSecureData = (
  encryptedData: string
): OrderSecure | undefined => {
  if (!secretKey || !encryptedData) {
    return undefined;
  }
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
    const parsedData = JSON.parse(decryptedData);
    return parsedData;
  } catch (error) {
    return undefined;
  }
};
